import moment from 'moment-timezone';
import { AvailabilityStatusConfig } from '@/contexts/dynamic-config';
import {
  Catalog_VariantPackGroupItemsExpandedFieldsFragment,
  ItemsContext_ItemModifiersFieldsFragment,
  SearchCatalog_VariantPackFieldsFragment,
  VariantPackOrderCount,
} from '@/types';
import { comparePacksByUnit } from '@/utils/pack-sort';
import { DEFAULT_LOCALE } from '@/utils/i18n';
import { getBestVariantPackVolumePromotion } from '@/utils/volume-promotions';
import {
  getGtinDescription,
  getGtinName,
  getGtinPhotoUrlList,
  GtinSettings,
} from '@/utils/gtin-metadata';
import {
  getPackDisplayString,
  isAddToOrderDisabled,
  isItemOutOfStock,
} from '@/utils/item';
import { getParamsFromNameValue } from '@/utils/salt-item';
import { getTrueTime } from '@/utils/true-time';
import { getVariantPackGroupParentUUIDs } from '@/utils/category';
import {
  GTINMetadata,
  ItemListOption,
  ItemListOptionMetadata,
  ModifierOption,
  PackModifierDisplayMap,
  PackModifierHashMap,
  VariantPackModifierGroup,
} from './item-context-types';
import {
  ItemDisplayOptionsWithSort,
  useItemDisplayOptions,
} from './new-order-context';
import { PackUnit } from '@/enums';
import { useAdsContext } from './ads-context';
import { useAvailabilityStatusEnabled } from '@/hooks/use-add-to-order-disabled';
import { useCallback, useMemo } from 'react';
import { useDynamicConfigValue } from '@/hooks/use-dynamic-config-value';
import { useItemQueryLocale } from '@/hooks/use-item-query-variables';
import {
  useVariantPackInfoContext,
  VariantPackInfoType,
} from './variant-pack-info-context';
import { useVolumePromotionsContext } from './volume-promotions-context';

interface MinAndMaxQuantity {
  maximumQuantity: number | null;
  minimumQuantity: number | null;
}

// Cut a locale such as "fr-CA" down to the 2-letter key the cache map uses.
// Fall back to English when there is no locale, so the key is always valid.
export const getSaltCacheLocaleKey = (
  locale: string | null | undefined,
): string => (locale || DEFAULT_LOCALE).slice(0, 2).toLowerCase();

// Guard against invalid jsonb data in the cache for image_urls and translations
export const getSaltCacheTranslation = (
  translations: unknown,
  localeKey: string,
): string | null => {
  if (typeof translations !== 'object' || translations === null) {
    return null;
  }

  const map = translations as Record<string, unknown>;

  const localeValue = map[localeKey];
  if (typeof localeValue === 'string' && localeValue) {
    return localeValue;
  }

  const fallbackValue = map[DEFAULT_LOCALE];
  return typeof fallbackValue === 'string' && fallbackValue
    ? fallbackValue
    : null;
};

export const getSaltCacheImageUrls = (imageUrls: unknown): string[] | null => {
  if (!Array.isArray(imageUrls)) {
    return null;
  }

  const urls = new Set<string>();
  for (let i = 0; i < imageUrls.length; i++) {
    const url = imageUrls[i];
    if (typeof url === 'string' && url) {
      urls.add(url);
    }
  }

  return [...urls];
};

export interface VariantPackPhotoFields {
  gtin_metadata?: { metadata?: unknown }[] | null;
  item?: { photo_url_list?: string[] | null } | null;
  salt?: { images: { uri?: string | null }[] } | null;
  salt_cache?: { image_urls?: unknown } | null;
}

interface VariantPackPhotoOptions {
  gtinSettings?: GtinSettings | null;
  saltApiIntegrationEnabled?: boolean;
}

export const getVariantPackPhotoURLs = (
  variantPack: VariantPackPhotoFields,
  { gtinSettings, saltApiIntegrationEnabled }: VariantPackPhotoOptions,
): string[] => {
  const photoUrls: string[] = variantPack.item?.photo_url_list ?? [];

  if (variantPack.salt_cache) {
    const imageUrls = getSaltCacheImageUrls(variantPack.salt_cache.image_urls);
    return imageUrls ?? photoUrls;
  }

  // Use SALT information if enabled, otherwise use GTIN metadata if available
  if (saltApiIntegrationEnabled) {
    if (variantPack.salt) {
      return [
        ...new Set(
          variantPack.salt.images
            .map((image) => image.uri)
            .filter((uri): uri is string => Boolean(uri)), // for typescript
        ),
      ];
    }
  } else if (gtinSettings) {
    const gtinMetadata = (variantPack.gtin_metadata?.[0]?.metadata ??
      null) as GTINMetadata | null;
    if (gtinMetadata) {
      return getGtinPhotoUrlList(gtinSettings, gtinMetadata, photoUrls);
    }
  }

  return photoUrls;
};

export const getMinAndMaxQuantity = (
  variantPackInfo: VariantPackInfoType | null,
): MinAndMaxQuantity => {
  if (!variantPackInfo?.constraints) {
    return {
      maximumQuantity: null,
      minimumQuantity: null,
    };
  }

  return {
    maximumQuantity: variantPackInfo.constraints.max_quantity ?? null,
    minimumQuantity: variantPackInfo.constraints.min_quantity ?? null,
  };
};

export const useAdaptVariantPackToItemListOption = () => {
  const itemDisplayOptions = useItemDisplayOptions();
  const {
    getVariantPackInfo,
    lastPurchaseInfoMap,
    parentVariantPackGroupMap,
    variantPackGroupMap,
  } = useVariantPackInfoContext();
  const { variantPackPromotionsMap } = useVolumePromotionsContext();
  const { sampleRequestMap } = useAdsContext();

  const saltApiIntegrationEnabled = useDynamicConfigValue(
    'salt_api_integration_enabled',
  );
  const queryLocale = useItemQueryLocale();

  const saltCacheLocaleKey = useMemo(() => {
    return getSaltCacheLocaleKey(queryLocale);
  }, [queryLocale]);

  const availabilityStatusEnabled = useAvailabilityStatusEnabled();

  return useCallback(
    (
      variantPack: SearchCatalog_VariantPackFieldsFragment,
      // Allows overriding the default promotion.
      // This is useful in scenarios where an item is part of multiple promotions and needs to display
      // the price associated with a specific promotion.
      promotionOverrideUUID?: string,
      variantPackOrderCounts?: VariantPackOrderCount[],
    ): ItemListOption => {
      const variantPackInfo = getVariantPackInfo(variantPack.uuid);
      const lastPurchaseInfo = lastPurchaseInfoMap[variantPack.uuid];

      const {
        displayItemCode,
        displayItemPrices,
        gtinSettings,
        itemModifiersEnabled,
      } = itemDisplayOptions;
      const { item, modifier_options, pack } = variantPack;

      const externalID = variantPack.external_item_id || null;
      const restaurantOverrides = item.restaurant_item_overrides[0];
      const unitPriceInMicros = displayItemPrices
        ? variantPackInfo?.priceInMicros
        : null;

      const photoURLs = getVariantPackPhotoURLs(variantPack, {
        gtinSettings,
        saltApiIntegrationEnabled,
      });
      let displayName = restaurantOverrides?.display_name || item.display_name;
      let description = item.description;

      if (variantPack.salt_cache) {
        const saltCache = variantPack.salt_cache;

        // TODO: drop the restaurant override lookup if we remove name
        // overrides. 43 items connected to non-deleted VPs have an override name,
        // which is why the cache does not store them. Until the feature goes
        // away, the client reads restaurant_item_overrides itself.
        displayName =
          restaurantOverrides?.display_name ||
          getSaltCacheTranslation(
            saltCache.name_translations,
            saltCacheLocaleKey,
          ) ||
          item.display_name;
        description =
          getSaltCacheTranslation(
            saltCache.description_translations,
            saltCacheLocaleKey,
          ) || description;
      }
      // Use SALT information if enabled, otherwise use GTIN metadata if available
      else if (saltApiIntegrationEnabled) {
        if (variantPack.salt) {
          description =
            getParamsFromNameValue(
              variantPack.salt.basicInformation.descriptions,
            ).Description ?? description;
          displayName =
            getParamsFromNameValue(variantPack.salt.basicInformation.names)
              .Name ?? displayName;
        }
      } else if (gtinSettings) {
        const gtinMetadata = (variantPack.gtin_metadata?.[0]?.metadata ??
          null) as GTINMetadata | null;
        if (gtinMetadata) {
          description = getGtinDescription(
            gtinSettings,
            gtinMetadata,
            description,
          );
          displayName = getGtinName(gtinSettings, gtinMetadata, displayName);
        }
      }

      const variantPackGroupCategoryUUIDs = getVariantPackGroupParentUUIDs(
        (variantPack.variant_pack_group_memberships || []).map(
          (member) => member.variant_pack_group_uuid,
        ),
        parentVariantPackGroupMap,
      );
      // Create a unique set of badges by extracting them from variantPackGroupMap,
      // ensuring that each badge appears only once based on its uuid
      const badges = [
        ...new Map(
          variantPackGroupCategoryUUIDs
            .flatMap((uuid) => variantPackGroupMap[uuid]?.badges || [])
            .map((badge) => [badge.uuid, badge]),
        ).values(),
      ];

      // If a promotionOverrideUUID is provided, use only that promotion
      let variantPackPromotions = variantPackPromotionsMap[variantPack.uuid];
      if (promotionOverrideUUID) {
        const promotionOverride = variantPackPromotions?.find(
          (p) => p.uuid === promotionOverrideUUID,
        );
        if (promotionOverride) {
          variantPackPromotions = [promotionOverride];
        }
      }

      // Use the promotion with the lowest price as the default
      const { promotionPricePerUnitInMicros, variantPackPromotion } =
        getBestVariantPackVolumePromotion(
          pack.unit_count,
          variantPackPromotions,
          unitPriceInMicros,
        );
      const promotedVariantPackInfo =
        variantPackPromotion?.promotedVariantPackInfo;
      const promotionPhotoURLs = promotedVariantPackInfo?.photoURLs ?? [];
      const { maximumQuantity, minimumQuantity } =
        getMinAndMaxQuantity(variantPackInfo);

      return {
        metadata: {
          ...getItemModifierGroupsAndModifierString(
            itemModifiersEnabled ? modifier_options : [],
          ),
          availabilityStatus:
            availabilityStatusEnabled && variantPackInfo?.availabilityStatus
              ? variantPackInfo.availabilityStatus
              : null,
          availableQuantity: variantPackInfo?.availableQuantity ?? null,
          badges,
          baseUnit: (pack.base_unit as PackUnit) || null,
          category: restaurantOverrides?.category || item.category || null,
          currencyCode: variantPackInfo?.currencyCode || null,
          description,
          displayName,
          externalID: displayItemCode ? externalID : null,
          extraKeywords: item.extra_keywords || '',
          itemUUID: item.uuid,
          leadTimeCutoffTime: variantPack.lead_time_cutoff_time ?? null,
          leadTimeDays: variantPack.lead_time_days ?? null,
          maximumQuantity,
          minimumQuantity,
          orderDate: lastPurchaseInfo?.orderDate,
          outOfStockUUIDs: new Set(),
          packDisplayString: getPackDisplayString({
            maxUnitSize: pack.max_unit_size,
            minUnitSize: pack.min_unit_size,
            unit: pack.unit as PackUnit,
          }),
          packUnit: pack.unit as PackUnit,
          photoURLs: [...promotionPhotoURLs, ...photoURLs],
          prebookConfig: variantPack.prebook_config,
          promotion: variantPackPromotion || null,
          promotionPricePerUnitInMicros,
          sampleRequest: sampleRequestMap[variantPack.uuid],
          skus: [],
          unitCount: pack.unit_count,
          unitPriceInMicros,
          upcList: variantPack.upc_list,
          variantPackGroupCategoryUUIDs,
          variantPackMetadata: variantPack.metadata,
          variantPackOrderCounts,
          variantPackUUID: variantPack.uuid,
        },
        title: displayName,
        value: variantPack.uuid,
      };
    },
    [
      availabilityStatusEnabled,
      getVariantPackInfo,
      itemDisplayOptions,
      lastPurchaseInfoMap,
      parentVariantPackGroupMap,
      saltApiIntegrationEnabled,
      saltCacheLocaleKey,
      sampleRequestMap,
      variantPackGroupMap,
      variantPackPromotionsMap,
    ],
  );
};

export type ExpandedGroupItemVariantPack = NonNullable<
  Catalog_VariantPackGroupItemsExpandedFieldsFragment['variant_pack_data']
>;
export type ExpandedGroupItemSaltData =
  Catalog_VariantPackGroupItemsExpandedFieldsFragment['salt_data'];

// Native adapter for the expanded catalog action's shape. Mirrors
// useAdaptVariantPackToItemListOption but reads salt from the sibling
// salt_data (flat) rather than variant_pack.salt, so the expanded response
// needs no mapping back onto the legacy variants_packs shape. Keep the
// metadata assembly in sync with the legacy adapter above.
export const useAdaptExpandedGroupItemToItemListOption = () => {
  const itemDisplayOptions = useItemDisplayOptions();
  const {
    getVariantPackInfo,
    lastPurchaseInfoMap,
    parentVariantPackGroupMap,
    variantPackGroupMap,
  } = useVariantPackInfoContext();
  const { variantPackPromotionsMap } = useVolumePromotionsContext();
  const { sampleRequestMap } = useAdsContext();

  const saltApiIntegrationEnabled = useDynamicConfigValue(
    'salt_api_integration_enabled',
  );
  const availabilityStatusEnabled = useAvailabilityStatusEnabled();

  return useCallback(
    (
      variantPack: ExpandedGroupItemVariantPack,
      saltData: ExpandedGroupItemSaltData,
    ): ItemListOption => {
      const variantPackInfo = getVariantPackInfo(variantPack.uuid);
      const lastPurchaseInfo = lastPurchaseInfoMap[variantPack.uuid];

      const {
        displayItemCode,
        displayItemPrices,
        gtinSettings,
        itemModifiersEnabled,
      } = itemDisplayOptions;
      const { item, modifier_options, pack } = variantPack;

      const externalID = variantPack.external_item_id || null;
      const restaurantOverrides = item?.restaurant_item_overrides[0];
      const unitPriceInMicros = displayItemPrices
        ? variantPackInfo?.priceInMicros
        : null;

      let photoURLs = item?.photo_url_list ?? [];
      let displayName =
        restaurantOverrides?.display_name || item?.display_name || '';
      let description = item?.description ?? null;
      // Use SALT information if enabled, otherwise use GTIN metadata if available
      if (saltApiIntegrationEnabled) {
        if (saltData) {
          description = saltData.description || description;
          photoURLs = [...new Set(saltData.image_urls ?? [])];
          displayName = saltData.name || displayName;
        }
      } else if (gtinSettings) {
        const gtinMetadata = (variantPack.gtin_metadata?.[0]?.metadata ??
          null) as GTINMetadata | null;
        if (gtinMetadata) {
          description = getGtinDescription(
            gtinSettings,
            gtinMetadata,
            description,
          );
          photoURLs = getGtinPhotoUrlList(
            gtinSettings,
            gtinMetadata,
            photoURLs,
          );
          displayName = getGtinName(gtinSettings, gtinMetadata, displayName);
        }
      }

      const variantPackGroupCategoryUUIDs = getVariantPackGroupParentUUIDs(
        (variantPack.variant_pack_group_memberships || []).map(
          (member) => member.variant_pack_group_uuid,
        ),
        parentVariantPackGroupMap,
      );
      const badges = [
        ...new Map(
          variantPackGroupCategoryUUIDs
            .flatMap((uuid) => variantPackGroupMap[uuid]?.badges || [])
            .map((badge) => [badge.uuid, badge]),
        ).values(),
      ];

      const { promotionPricePerUnitInMicros, variantPackPromotion } =
        getBestVariantPackVolumePromotion(
          pack?.unit_count ?? 1,
          variantPackPromotionsMap[variantPack.uuid],
          unitPriceInMicros,
        );
      const promotedVariantPackInfo =
        variantPackPromotion?.promotedVariantPackInfo;
      const promotionPhotoURLs = promotedVariantPackInfo?.photoURLs ?? [];
      const { maximumQuantity, minimumQuantity } =
        getMinAndMaxQuantity(variantPackInfo);

      // Normalize the expanded modifier options onto the legacy fragment shape
      // the modifier-string helper expects (coalescing the nullable fields).
      const modifierOptions: ItemsContext_ItemModifiersFieldsFragment[] = (
        itemModifiersEnabled ? modifier_options : []
      ).map((modifierOption) => ({
        modifier_option: {
          modifier_option_group: {
            name:
              modifierOption.modifier_option.modifier_option_group?.name ?? '',
            sort_order:
              modifierOption.modifier_option.modifier_option_group
                ?.sort_order ?? null,
            uuid:
              modifierOption.modifier_option.modifier_option_group?.uuid ?? '',
          },
          name: modifierOption.modifier_option.name ?? '',
          sort_order: modifierOption.modifier_option.sort_order ?? null,
          uuid: modifierOption.modifier_option.uuid,
        },
      }));

      return {
        metadata: {
          ...getItemModifierGroupsAndModifierString(modifierOptions),
          availabilityStatus:
            availabilityStatusEnabled && variantPackInfo?.availabilityStatus
              ? variantPackInfo.availabilityStatus
              : null,
          availableQuantity: variantPackInfo?.availableQuantity ?? null,
          badges,
          baseUnit: (pack?.base_unit as PackUnit) || null,
          category: restaurantOverrides?.category || item?.category || null,
          currencyCode: variantPackInfo?.currencyCode || null,
          description,
          displayName,
          externalID: displayItemCode ? externalID : null,
          extraKeywords: item?.extra_keywords || '',
          itemUUID: item?.uuid ?? '',
          leadTimeCutoffTime: variantPack.lead_time_cutoff_time ?? null,
          leadTimeDays: variantPack.lead_time_days ?? null,
          maximumQuantity,
          minimumQuantity,
          orderDate: lastPurchaseInfo?.orderDate,
          outOfStockUUIDs: new Set<string>(),
          packDisplayString: getPackDisplayString({
            maxUnitSize: pack?.max_unit_size ?? 0,
            minUnitSize: pack?.min_unit_size ?? 0,
            unit: (pack?.unit ?? '') as PackUnit,
          }),
          packUnit: (pack?.unit ?? '') as PackUnit,
          photoURLs: [...promotionPhotoURLs, ...photoURLs],
          prebookConfig: variantPack.prebook_config,
          promotion: variantPackPromotion || null,
          promotionPricePerUnitInMicros,
          sampleRequest: sampleRequestMap[variantPack.uuid],
          skus: [],
          unitCount: pack?.unit_count ?? 1,
          unitPriceInMicros,
          upcList: variantPack.upc_list ?? null,
          variantPackGroupCategoryUUIDs,
          variantPackMetadata: variantPack.metadata,
          variantPackUUID: variantPack.uuid,
        },
        title: displayName,
        value: variantPack.uuid,
      };
    },
    [
      availabilityStatusEnabled,
      getVariantPackInfo,
      itemDisplayOptions,
      lastPurchaseInfoMap,
      parentVariantPackGroupMap,
      saltApiIntegrationEnabled,
      sampleRequestMap,
      variantPackGroupMap,
      variantPackPromotionsMap,
    ],
  );
};

export const groupItemListOptions = (
  items: ItemListOption[],
  displayOptions: ItemDisplayOptionsWithSort,
): ItemListOption[] => {
  const { displayItemCode, itemModifiersEnabled } = displayOptions;
  if (!displayItemCode) {
    return items.map(adaptVariantPackMetadata);
  }

  const groupedItemMap: { [itemID: string]: ItemListOption } = {};
  return items
    .reduce<ItemListOption[]>((acc, item) => {
      const itemID = itemModifiersEnabled
        ? item.metadata!.itemUUID
        : item.metadata!.externalID;
      if (itemID) {
        if (groupedItemMap[itemID]) {
          groupedItemMap[itemID].metadata!.skus.push(item.metadata!);
        } else {
          const listItem = {
            metadata: {
              ...item.metadata!,
              skus: [item.metadata!],
            },
            title: item.title,
            value: item.value,
          };
          groupedItemMap[itemID] = listItem;
          acc.push(listItem);
        }
      } else {
        acc.push({
          metadata: {
            ...item.metadata!,
            skus: [item.metadata!],
          },
          title: item.title,
          value: item.value,
        });
      }
      return acc;
    }, [])
    .map((item) => {
      item.metadata!.outOfStockUUIDs = new Set(
        item.metadata!.skus.reduce<string[]>((result, sku) => {
          if (isItemOutOfStock(sku.availableQuantity)) {
            result.push(sku.variantPackUUID);
          }

          return result;
        }, []),
      );

      // Sort the pack list by the canonical / configured pack order.
      item.metadata!.skus.sort((a, b) =>
        comparePacksByUnit(
          {
            displayString: a.packDisplayString!,
            id: a.variantPackUUID,
            unit: a.packUnit,
          },
          {
            displayString: b.packDisplayString!,
            id: b.variantPackUUID,
            unit: b.packUnit,
          },
          displayOptions.packSortConfig,
        ),
      );

      // Use photoURLs from first SKU that has images
      // This preserves the intended photo ordering strategy (SALT-first vs distributor-first)
      // instead of merging all photos which would lose the configured ordering
      if (item.metadata!.skus.length > 1 && !item.metadata!.photoURLs?.length) {
        for (const sku of item.metadata!.skus) {
          if (sku.photoURLs?.length) {
            item.metadata!.photoURLs = sku.photoURLs;
            break;
          }
        }
      }

      // The tile headline follows the first pack in sorted order (price, UOM, SKU, name, add target). The image stays on the backend representative, and the pack list + stock set are group-level, so those three are kept as-is.
      const [displayVariantPack] = item.metadata!.skus;
      if (
        displayVariantPack.variantPackUUID !== item.metadata!.variantPackUUID
      ) {
        item.metadata = {
          ...displayVariantPack,
          outOfStockUUIDs: item.metadata!.outOfStockUUIDs,
          photoURLs: item.metadata!.photoURLs,
          skus: item.metadata!.skus,
        };
        item.title = displayVariantPack.displayName;
        item.value = displayVariantPack.variantPackUUID;
      }

      return item;
    });
};

export const getItemModifierGroupsAndModifierString = (
  variantModifierOptions: ItemsContext_ItemModifiersFieldsFragment[],
): {
  modifierGroups: VariantPackModifierGroup[];
  modifierString: string;
} => {
  const modifierNames: string[] = [];
  const modifierGroupMap = [...variantModifierOptions]
    // Have to sort modifier groups client side since there
    // are multiple `sort_order` nested references in Hasura
    // https://github.com/hasura/graphql-engine/issues/3796
    .sort((a, b) =>
      typeof a.modifier_option.modifier_option_group.sort_order === 'number' &&
      typeof b.modifier_option.modifier_option_group.sort_order === 'number'
        ? a.modifier_option.modifier_option_group.sort_order -
          b.modifier_option.modifier_option_group.sort_order
        : a.modifier_option.modifier_option_group.name.localeCompare(
            b.modifier_option.modifier_option_group.name,
          ),
    )
    .reduce<{
      [groupUUID: string]: VariantPackModifierGroup;
    }>((acc, vmo) => {
      const { modifier_option_group, name, sort_order, uuid } =
        vmo.modifier_option;
      if (!acc[modifier_option_group.uuid]) {
        acc[modifier_option_group.uuid] = {
          groupName: modifier_option_group.name,
          groupSortOrder: modifier_option_group.sort_order ?? null,
          groupUUID: modifier_option_group.uuid,
          option: {
            modifierName: name,
            modifierSortOrder: sort_order ?? null,
            modifierUUID: uuid,
          },
        };
      }
      modifierNames.push(name);
      return acc;
    }, {});
  return {
    modifierGroups: Object.values(modifierGroupMap),
    modifierString: modifierNames.join(', '),
  };
};

export const getPackModifierMaps = (
  metadata: ItemListOptionMetadata,
): {
  packModifierDisplayMap: PackModifierDisplayMap;
  packModifierHashMap: PackModifierHashMap;
} => {
  const keyedPackModifierDisplayMap: {
    [packDisplayString: string]: {
      [groupUUID: string]: {
        groupName: string;
        groupUUID: string;
        options: ModifierOption[];
      };
    };
  } = {};
  const packModifierHashMap: PackModifierHashMap = {};
  const seenModifierSetMap: {
    [packDisplayString: string]: {
      [groupUUID: string]: Set<string>;
    };
  } = {};

  metadata.skus.forEach((sku) => {
    const {
      baseUnit,
      currencyCode,
      externalID,
      modifierGroups,
      packDisplayString,
      promotionPricePerUnitInMicros,
      unitCount,
      unitPriceInMicros,
      variantPackUUID,
    } = sku;
    const vpModifierUUIDs: string[] = [];

    modifierGroups?.forEach((group) => {
      const { groupName, groupUUID, option } = group;
      vpModifierUUIDs.push(option.modifierUUID);

      // Adds each group's modifier options into keyed pack display map as
      // keyed map allows for faster lookup of pack -> group -> modifier
      keyedPackModifierDisplayMap[packDisplayString] =
        keyedPackModifierDisplayMap[packDisplayString] || {};

      keyedPackModifierDisplayMap[packDisplayString][groupUUID] =
        keyedPackModifierDisplayMap[packDisplayString][groupUUID] || {
          groupName,
          groupUUID,
          options: [option],
        };

      seenModifierSetMap[packDisplayString] =
        seenModifierSetMap[packDisplayString] || {};

      seenModifierSetMap[packDisplayString][groupUUID] =
        seenModifierSetMap[packDisplayString][groupUUID] ||
        new Set([option.modifierUUID]);

      if (
        !seenModifierSetMap[packDisplayString][groupUUID].has(
          option.modifierUUID,
        )
      ) {
        keyedPackModifierDisplayMap[packDisplayString][groupUUID].options.push(
          option,
        );
        seenModifierSetMap[packDisplayString][groupUUID].add(
          option.modifierUUID,
        );
      }
    });

    // Adds each vp's modifier options to pack hash map
    const hashedModifierUUIDs = getHashFromModifierUUIDs(vpModifierUUIDs);
    const variantPackInfo = {
      baseUnit: baseUnit ?? null,
      currencyCode: currencyCode ?? null,
      externalID: externalID ?? null,
      promotionPricePerUnitInMicros: promotionPricePerUnitInMicros ?? null,
      unitCount,
      unitPriceInMicros: unitPriceInMicros ?? null,
      variantPackUUID,
    };

    packModifierHashMap[packDisplayString] =
      packModifierHashMap[packDisplayString] || {};

    packModifierHashMap[packDisplayString][hashedModifierUUIDs] =
      variantPackInfo;
  });

  return {
    // Restructure keyed map's groups and sorted options
    // into arrays to enforce deterministic ordering
    packModifierDisplayMap: Object.keys(keyedPackModifierDisplayMap).reduce(
      (acc, packStr) => ({
        ...acc,
        [packStr]: Object.values(keyedPackModifierDisplayMap[packStr]).map(
          (displayMap) => ({
            ...displayMap,
            options: displayMap.options.sort((a, b) =>
              a.modifierSortOrder && b.modifierSortOrder
                ? a.modifierSortOrder - b.modifierSortOrder
                : a.modifierName.localeCompare(b.modifierName),
            ),
          }),
        ),
      }),
      {},
    ),
    packModifierHashMap,
  };
};

export const getHashFromModifierUUIDs = (uuids: string[]): number => {
  let hash = 0;
  // Bitwise hashing function inspired by
  // https://stackoverflow.com/questions/6122571/simple-non-secure-hash-function-for-javascript
  for (let uuid of uuids) {
    for (let i = 0; i < uuid.length; i++) {
      const char = uuid.charCodeAt(i);

      /* eslint-disable no-bitwise */
      hash = (hash << 5) - hash + char;
      hash &= hash;
      /* eslint-enable no-bitwise */
    }
  }
  return hash;
};

const isSkuAvailable = (
  ilom: ItemListOptionMetadata,
  availabilityStatusConfig?: AvailabilityStatusConfig,
) =>
  !isAddToOrderDisabled({
    availabilityStatus: ilom.availabilityStatus,
    availabilityStatusConfig,
    availableQuantity: ilom.availableQuantity,
    // Set to null to sort orders the same way regardless of role
    supplierEmployeeRole: null,
  });

const isItemAvailable = (
  ilom?: ItemListOptionMetadata,
  availabilityStatusConfig?: AvailabilityStatusConfig,
) => {
  if (!ilom) {
    return false;
  }

  if (isSkuAvailable(ilom, availabilityStatusConfig)) {
    return true;
  }

  // Fall back to child skus (e.g. a grouped item's other pack sizes)
  return ilom.skus.some((sku) => isSkuAvailable(sku, availabilityStatusConfig));
};

export const sortItemListOptionsByAvailability = (
  itemListOptions: ItemListOption[],
  availabilityStatusConfig?: AvailabilityStatusConfig,
): ItemListOption[] => {
  const available: ItemListOption[] = [];
  const unavailable: ItemListOption[] = [];

  // Sort on availability_status only when it's enabled
  const sortConfig = availabilityStatusConfig?.sort_disabled_statuses_last
    ? availabilityStatusConfig
    : undefined;

  for (let i = 0; i < itemListOptions.length; i++) {
    const ilo = itemListOptions[i];

    if (isItemAvailable(ilo.metadata, sortConfig)) {
      available.push(ilo);
    } else {
      unavailable.push(ilo);
    }
  }
  return [...available, ...unavailable];
};

export const getSearchFieldsFromMetadata = (item: ItemListOption) => {
  const metadata = item.metadata;

  const upcList = metadata?.upcList || [];
  return [
    item.title,
    metadata?.externalID,
    metadata?.description,
    metadata?.category,
    metadata?.packUnit,
    metadata?.extraKeywords,
    ...upcList,
  ]
    .filter(Boolean)
    .map(String);
};

export const removeAdComponents = (
  originalMetadata: ItemListOptionMetadata,
): ItemListOptionMetadata => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { displayAd, paidSearch, sampleRequest, ...nextMetadata } =
    originalMetadata;
  return nextMetadata;
};

export const adaptVariantPackMetadata = (item: ItemListOption) => {
  return {
    ...item,
    metadata: {
      ...item.metadata!,
      outOfStockUUIDs: isItemOutOfStock(item.metadata!.availableQuantity)
        ? new Set<string>([item.metadata!.variantPackUUID])
        : new Set<string>(),
      skus: [item.metadata!],
    },
  };
};

export const addToOrderPrebookItemDisabled = (
  item: ItemListOptionMetadata,
  supplierTimezone: string,
) => {
  if (item.prebookConfig) {
    const nowMoment = moment(getTrueTime()).tz(supplierTimezone);
    const momentFrom = moment(item.prebookConfig.order_from).startOf('day');
    const momentUntil = moment(item.prebookConfig.order_until).endOf('day');
    return nowMoment.isBefore(momentFrom) || nowMoment.isAfter(momentUntil);
  }
  return false;
};
